import { palette } from '@openx/components-core/lib/theme/paletteV2';
import type { DealInsightsSpendApi } from '@openx/types/dealInsights';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { buildLabel, buildTicks, XAxisTick, YAxisTick } from '../../chartUtils';

type SpendChartCoreProps = {
  values: DealInsightsSpendApi[];
  isLoading?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  tooltipContent?: ReactNode;
  isAnimationActive?: boolean;
};

export const SpendChartCore = ({
  values,
  isLoading = false,
  showLegend = false,
  showTooltip = false,
  tooltipContent,
  isAnimationActive = true,
}: SpendChartCoreProps) => {
  const { t } = useTranslation();

  const spendMax = values.reduce((max, entry) => Math.max(max, entry?.spend ?? 0), 0);
  const ecpmMax = values.reduce((max, entry) => Math.max(max, entry?.ecpm ?? 0), 0);
  const spendScale = buildTicks(spendMax);
  const ecpmScale = buildTicks(ecpmMax);

  return (
    <ResponsiveContainer>
      <ComposedChart data={values} style={{ overflow: 'visible' }} margin={{ top: 28, right: 0, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke={palette.divider} />
        {showLegend && <Legend verticalAlign="top" height={36} />}
        <XAxis
          dataKey="date"
          stroke={palette.divider}
          height={60}
          tick={props => <XAxisTick {...props} isLoading={isLoading} />}
          {...(isLoading && { tickCount: 5, interval: 0 })}
        />
        <YAxis
          yAxisId="left"
          orientation="left"
          stroke="transparent"
          label={{ ...buildLabel(t('Spend')), fill: isLoading ? palette.divider : palette.text.primary }}
          domain={spendScale.domain}
          ticks={spendScale.ticks}
          tick={props => (
            <YAxisTick
              {...props}
              isLoading={isLoading}
              isLeftAxis={true}
              leftScale={spendScale}
              rightScale={ecpmScale}
              formatter={val => `$${abbreviateNumericValue(val, { allowZero: true })}`}
            />
          )}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          stroke="transparent"
          label={{ ...buildLabel(t('CPM')), fill: isLoading ? palette.divider : palette.text.primary }}
          domain={ecpmScale.domain}
          ticks={ecpmScale.ticks}
          tick={props => (
            <YAxisTick
              {...props}
              isLoading={isLoading}
              isLeftAxis={false}
              leftScale={spendScale}
              rightScale={ecpmScale}
              formatter={val => `$${abbreviateNumericValue(val, { allowZero: true })}`}
            />
          )}
        />
        {showTooltip && tooltipContent}
        <Bar
          yAxisId="left"
          dataKey="spend"
          name={t('Spend')}
          fill={isLoading ? palette.divider : palette.charts.blue}
          barSize={125}
          style={{ opacity: isLoading ? 0.5 : 1 }}
          isAnimationActive={isAnimationActive}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="ecpm"
          name={t('CPM')}
          stroke={isLoading ? palette.divider : palette.charts.pink}
          strokeWidth={2}
          dot={{ r: 4, fill: isLoading ? palette.divider : palette.charts.pink }}
          activeDot={{
            r: 5,
            fill: palette.common.white,
            stroke: isLoading ? palette.divider : palette.charts.pink,
            strokeWidth: 2,
          }}
          isAnimationActive={isAnimationActive}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
